class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name"),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

document.addEventListener("DOMContentLoaded", () => {
  const elements = {
    vendorInput: document.getElementById("CartVendorCode"),
    discountInput: document.getElementById("CartDiscountCode"),
    applyVendorButton: document.getElementById("applyVendorCodeButton"),
    removeVendorButton: document.getElementById("removeDiscountCodeVendorButton"),
    applyDiscountButton: document.getElementById("applyDiscountCodeButton"),
    removeDiscountButton: document.getElementById("removeDiscountCodeButton"),
    discountFeedback: document.getElementById("discountFeedback"),
    discountFeedbackText: document.getElementById("discountFeedbackText"),
    vendorFeedback: document.getElementById("discountFeedbackVendor"),
    vendorFeedbackText: document.getElementById("discountFeedbackVendorText"),
  };

  const cleanCoupon = (raw) => raw
    .replace(/\s*\(-R\$.*?\)/, "")
    .replace(/×/g, "")
    .replace(/\s+/g, "")
    .trim();

  const showFeedback = (container, textContainer, code, discountValue = null) => {
    if (!container || !textContainer) return;

    const discountMessage = discountValue ? ` – desconto de R$ ${discountValue}` : '';
    const message = `
      <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.2rem; height: 1.2rem; fill: #246b3d;" viewBox="0 0 24 24">
        <path d="M9 16.2l-3.5-3.6L4 14.1l5 5 12-12-1.4-1.4z"/>
      </svg> Cupom "${code}" aplicado${discountMessage}
    `;

    textContainer.innerHTML = message;
    container.style.display = "flex";
  };

  const hideFeedback = (container) => {
    if (container) container.style.display = "none";
  };

  const updateCartDiscounts = async ({ addCode = null, removeCode = null } = {}) => {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      const existingCodes = cart.discount_codes
        .filter(coupon => coupon.applicable)
        .map(coupon => coupon.code);

      const discountCodes = new Set(existingCodes);

      if (addCode) discountCodes.add(addCode);
      if (removeCode) discountCodes.delete(removeCode);

      const allCodes = Array.from(discountCodes).filter(Boolean);

      await fetch(`/checkout?discount=${allCodes.join(",")}`);

      window.location.href = '/cart';
    } catch (error) {
      console.error('Erro ao atualizar cupons:', error);
      alert('Ocorreu um erro ao aplicar o cupom. Por favor, tente novamente.');
    }
  };

  const applyCoupon = (code) => {
    if (!code) {
      alert("Digite o código de desconto ou vendedor.");
      return;
    }
    updateCartDiscounts({ addCode: code });
  };

  const removeCoupon = (code) => {
    if (!code) return;
    updateCartDiscounts({ removeCode: code });
  };

  const setupButton = (button, action) => {
    if (button) {
      button.addEventListener("click", action);
    }
  };

  const syncInputsAndFeedback = () => {
    try {
      // Busca o cupom de vendedor via classe
      const vendorDiscountElement = document.querySelector(".discounts__discount--position");
      const productDiscountElement = [...document.querySelectorAll(".discounts__discount")]
        .filter(el => !el.classList.contains("discounts__discount--position"))[0]; // O primeiro que não for vendedor

      const getCouponText = (el) => {
        if (!el) return null;
        const rawText = el.textContent || "";
        return rawText.replace(/\s*\(-R\$.*?\)/, "").trim();
      };

      const vendorCode = getCouponText(vendorDiscountElement);
      const productCode = getCouponText(productDiscountElement);

      // Preencher os inputs
      if (elements.vendorInput) elements.vendorInput.value = vendorCode || "";
      if (elements.discountInput) elements.discountInput.value = productCode || "";

      // Mostrar feedbacks
      if (vendorCode) {
        showFeedback(elements.vendorFeedback, elements.vendorFeedbackText, vendorCode);
      } else {
        hideFeedback(elements.vendorFeedback);
      }

      if (productCode) {
        showFeedback(elements.discountFeedback, elements.discountFeedbackText, productCode);
      } else {
        hideFeedback(elements.discountFeedback);
      }
    } catch (error) {
      console.error("Erro ao sincronizar inputs e feedbacks:", error);
    }
  };

  // Configura eventos
  setupButton(elements.applyVendorButton, () => {
    const code = cleanCoupon(elements.vendorInput?.value || "");
    applyCoupon(code);
  });

  setupButton(elements.applyDiscountButton, () => {
    const code = cleanCoupon(elements.discountInput?.value || "");
    applyCoupon(code);
  });

  setupButton(elements.removeVendorButton, () => {
    const code = cleanCoupon(elements.vendorInput?.value || "");
    removeCoupon(code);
  });

  setupButton(elements.removeDiscountButton, () => {
    const code = cleanCoupon(elements.discountInput?.value || "");
    removeCoupon(code);
  });

  // Sincroniza os inputs e os feedbacks ao carregar
  syncInputsAndFeedback();
});
